/**
 * services/DeviceService.ts
 * Device management and registration service for sync tracking
 *
 * Handles business logic for device registration, updates, and sync tracking.
 * All database access delegated to userSyncDevicesRepository.
 */

import { userSyncDevicesRepository } from '@/repositories/userSyncDevicesRepository';
import { supabase } from '@/lib/supabase';
import { toDomain } from '@/lib/supabaseMappers';
import type {
  UserSyncDevice,
  UserSyncDeviceInsert
} from '@/types/supabase/UserSyncDevice';
import type { SyncProviderType } from '@/types/sync/SyncProvider';

/**
 * Device Service
 *
 * Provides business logic for device registration and sync tracking.
 * Orchestrates upsert operations, validation, and logging.
 * All data access delegated to repository layer.
 */
export class DeviceService {

  // ===== SPECIALIZED DEVICE OPERATIONS =====

  /**
   * Get device by user ID and device ID
   *
   * @param userId - User ID to search for
   * @param deviceId - Device ID to search for
   * @returns Promise resolving to device or null if not found
   */
  async getDeviceByDeviceId(
    userId: string,
    deviceId: string
  ): Promise<UserSyncDevice | null> {
    try {
      return await userSyncDevicesRepository.getByDeviceId(userId, deviceId);
    } catch (error) {
      console.error(
        `❌ DeviceService: Failed to get device for user ${userId}, device ${deviceId}:`,
        error
      );
      return null;
    }
  }

  /**
   * Get all devices for a user
   *
   * @param userId - User ID to get devices for
   * @returns Promise resolving to array of user's devices
   */
  async getUserDevices(userId: string): Promise<UserSyncDevice[]> {
    try {
      return await userSyncDevicesRepository.getByUserId(userId);
    } catch (error) {
      console.error(
        `❌ DeviceService: Failed to get devices for user ${userId}:`,
        error
      );
      return [];
    }
  }

  /**
   * Register or update device for sync tracking
   *
   * If device already exists (userId + deviceId combination), updates only mutable
   * presentation fields (deviceName, syncProvider).
   * If device is new, creates a new device record with canonical "first-seen" state.
   *
   * NOTE: We intentionally do NOT reset directoryHandleStored or lastSyncAt
   * on re-register. A returning device should keep those lifecycle flags.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Unique device identifier (UUID)
   * @param deviceName - User-friendly device name (e.g., "Chrome on MacOS")
   * @param syncProvider - Sync provider type (defaults to 'local')
   * @returns Promise resolving to the device record
   */
  async registerDevice(
    userId: string,
    deviceId: string,
    deviceName: string,
    syncProvider: SyncProviderType = 'local'
  ): Promise<UserSyncDevice> {
    try {
      // Check if device already exists
      const existingDevice = await this.getDeviceByDeviceId(userId, deviceId);

      if (existingDevice) {
        // Device exists - update only syncProvider, preserve custom device name
        console.log(
          `📱 DeviceService: Updating existing device ${deviceId} for user ${userId}`
        );

        const updated = await userSyncDevicesRepository.update(userId, deviceId, {
          syncProvider
          // NOTE: Don't update deviceName - preserve user's custom name
        });

        console.log(
          `✅ DeviceService: Device re-registered - ${existingDevice.deviceName} (${deviceId})`
        );
        return updated;
      } else {
        // New device - create record with canonical "first-seen" state
        console.log(
          `📱 DeviceService: Registering new device ${deviceId} for user ${userId}`
        );

        const deviceData: UserSyncDeviceInsert = {
          userId,
          deviceId,
          deviceName,
          syncProvider,
          lastSyncAt: null, // Will be set on first sync completion
          directoryHandleStored: false
        };

        const created = await userSyncDevicesRepository.create(deviceData);

        console.log(
          `✅ DeviceService: Device registered - ${deviceName} (${deviceId})`
        );
        return created;
      }
    } catch (error) {
      console.error(
        `❌ DeviceService: Failed to register device ${deviceId} for user ${userId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Update last sync timestamp for a device
   *
   * Timestamp must be ISO 8601 format to keep DB values clean and sortable.
   * Always uses new Date().toISOString() for timestamp format.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @returns Promise resolving when update completes
   */
  async updateLastSync(userId: string, deviceId: string): Promise<void> {
    try {
      const device = await this.getDeviceByDeviceId(userId, deviceId);

      if (!device) {
        console.warn(
          `⚠️ DeviceService: Device not found for lastSyncAt update: ${deviceId}`
        );
        return;
      }

      await userSyncDevicesRepository.updateLastSync(
        userId,
        deviceId,
        new Date().toISOString()
      );

      console.log(
        `✅ DeviceService: Updated lastSyncAt for device ${deviceId}`
      );
    } catch (error) {
      console.error(
        `❌ DeviceService: Failed to update lastSyncAt for device ${deviceId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Update export/import timestamps for a device
   *
   * Combined method for efficiency (single DB call when both timestamps update).
   * Only updates provided timestamps; omitted fields are not touched.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @param timestamps - Object with optional exportedAt and/or importedAt ISO timestamps
   */
  async updateSyncTimestamps(
    userId: string,
    deviceId: string,
    timestamps: { exportedAt?: string; importedAt?: string }
  ): Promise<void> {
    // Guard: don't call repository if nothing to update
    if (!timestamps.exportedAt && !timestamps.importedAt) {
      return;
    }

    try {
      await userSyncDevicesRepository.updateSyncTimestamps(
        userId,
        deviceId,
        timestamps
      );

      const parts = [];
      if (timestamps.exportedAt) parts.push('lastExportedAt');
      if (timestamps.importedAt) parts.push('lastImportedAt');
      console.log(
        `✅ DeviceService: Updated ${parts.join(', ')} for device ${deviceId}`
      );
    } catch (error) {
      console.error(
        `❌ DeviceService: Failed to update sync timestamps for device ${deviceId}:`,
        error
      );
      // Don't rethrow - this is fire-and-forget, don't block sync completion
    }
  }

  /**
   * Update device name
   *
   * Allows users to customize their device names for easier identification.
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @param newName - New device name
   * @returns Promise resolving to updated device
   */
  async updateDeviceName(
    userId: string,
    deviceId: string,
    newName: string
  ): Promise<UserSyncDevice> {
    try {
      const device = await this.getDeviceByDeviceId(userId, deviceId);

      if (!device) {
        throw new Error(`Device not found: ${deviceId}`);
      }

      const updated = await userSyncDevicesRepository.update(userId, deviceId, {
        deviceName: newName
      });

      console.log(
        `✅ DeviceService: Updated device name to "${newName}" for device ${deviceId}`
      );

      return updated;
    } catch (error) {
      console.error(
        `❌ DeviceService: Failed to update device name for device ${deviceId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Update directory handle stored flag
   *
   * Called when a directory handle is successfully stored in IndexedDB
   * for File System Access API integration
   *
   * @param userId - User ID who owns the device
   * @param deviceId - Device ID to update
   * @param stored - Whether directory handle is stored
   * @returns Promise resolving when update completes
   */
  async updateDirectoryHandleStored(
    userId: string,
    deviceId: string,
    stored: boolean
  ): Promise<void> {
    try {
      const device = await this.getDeviceByDeviceId(userId, deviceId);

      if (!device) {
        console.warn(
          `⚠️ DeviceService: Device not found for directoryHandleStored update: ${deviceId}`
        );
        return;
      }

      await userSyncDevicesRepository.updateDirectoryHandleStored(
        userId,
        deviceId,
        stored
      );

      console.log(
        `✅ DeviceService: Updated directoryHandleStored=${stored} for device ${deviceId}`
      );
    } catch (error) {
      console.error(
        `❌ DeviceService: Failed to update directoryHandleStored for device ${deviceId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Delete device by ID
   *
   * Removes device record for the specified database ID.
   * Looks up the device first to get userId and deviceId for secure deletion.
   *
   * @param dbId - Database primary key (UUID)
   * @returns Promise resolving when deletion completes
   */
  async delete(dbId: string): Promise<void> {
    try {
      // Lookup device by database PK to get user/device IDs for secure deletion
      const device = await userSyncDevicesRepository.getByDbId(dbId);

      if (!device) {
        throw new Error(`Device not found: ${dbId}`);
      }

      await userSyncDevicesRepository.delete(device.userId, device.deviceId);

      console.log(`✅ DeviceService: Device deleted - ${dbId}`);
    } catch (error) {
      console.error(`❌ DeviceService: Failed to delete device ${dbId}:`, error);
      throw error;
    }
  }

  // ===== REALTIME SUBSCRIPTION METHODS =====
  // NEW FOR TASK 2.9: Service-managed realtime subscriptions (Option 2 architecture)

  /**
   * Subscribe to device list changes via realtime
   *
   * Service-managed subscription pattern:
   * - Service creates/tears down the Supabase channel
   * - Service normalizes wire data → domain data
   * - Service handles INSERT/UPDATE/DELETE events
   * - Component receives updated list via callback
   *
   * @param userId - User ID to filter subscription
   * @param onDevicesUpdate - Callback invoked when device list changes
   * @returns Unsubscribe function to clean up channel
   */
  subscribeToDeviceChanges(
    userId: string,
    onDevicesUpdate: (action: {
      type: 'INSERT' | 'UPDATE' | 'DELETE';
      device?: UserSyncDevice;
      deletedId?: string;
    }) => void
  ): () => void {
    const channel = supabase
      .channel(`device-changes-${userId}`)
      .on('postgres_changes', {
        event: '*',  // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'user_sync_devices',
        filter: `user_id=eq.${userId}`
      }, (payload) => {
        console.log('🔄 Realtime: Device updated', payload);

        try {
          if (payload.eventType === 'DELETE') {
            // Notify component to remove device
            onDevicesUpdate({
              type: 'DELETE',
              deletedId: payload.old['id'] as string
            });
          } else {
            // INSERT or UPDATE - convert wire → domain
            const device = this.applyRealtimeUpdate(payload.new);

            onDevicesUpdate({
              type: payload.eventType as 'INSERT' | 'UPDATE',
              device
            });
          }
        } catch (error) {
          console.error('Failed to apply device realtime update:', error);
        }
      })
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(channel);
    };
  }

  /**
   * Apply realtime update from Supabase postgres_changes event (PRIVATE HELPER)
   *
   * Converts wire (snake_case) → domain (camelCase)
   * No cache needed (devices live server-side only)
   *
   * @param wireData - Raw payload.new from postgres_changes event
   * @returns Domain-typed UserSyncDevice
   */
  private applyRealtimeUpdate(wireData: unknown): UserSyncDevice {
    // Convert wire (snake_case) to domain (camelCase)
    return toDomain<UserSyncDevice>(wireData);
  }
}

/**
 * Singleton instance of the device service
 */
export const deviceService = new DeviceService();
